// Named sidebar data providers for the Projects app.
//
// A provider exists for exactly one reason: the data (or the control that
// scopes it) is irreducibly complex — an RBAC join, a preference-ordered list,
// a cross-field count — so a declarative `{ api, map }` can't express it. It
// returns already-shaped rows plus the gateway-specific interactive bits
// (drag-reorder persistence, right-click context menu, icon/colour popover,
// scope toggle) as injected nodes, which keeps every panel CONFIG pure JSON.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { P, PROJECT_TABS, usePermission } from '@clarix/rbac';
import { useAuthStore } from '@clarix/auth-store';
import { useProjectsList } from '@clarix/services-projects';
import { getProjectBadgeByCode, getUserProjectBadge } from '@clarix/utils';
import type {
  ProjectListItem,
  RowConfig,
  SidebarProviderResult,
} from '@clarix/shared-types';
import { useSidebarProjectPreferences } from '../useSidebarProjectPreferences';
import { sortSidebarProjects } from '../sidebarSort';
import { SidebarProjectContextMenu } from '../SidebarProjectContextMenu';
import { SidebarProjectEditPopover } from '../SidebarProjectEditPopover';
import { PanelToggle } from '../engine/primitives';
import { useCalendarShellAccess } from '../../quick-meeting/useCalendarShellAccess';

/** Project section tab → the engine icon name for its sidebar sub-row. */
const TAB_ICON_NAME: Record<string, string> = {
  overview: 'BarChart3',
  modules: 'Layers',
  features: 'Puzzle',
  tasks: 'CheckSquare',
  meetings: 'Video',
  team: 'Users',
  settings: 'Settings',
};

/* ────────────────────────── My Projects tree ────────────────────────── */

/**
 * The projects tree: scoped (mine / all), sorted pinned-first then custom order,
 * each row carrying its RBAC-gated section tabs, pin marker and role badge.
 *
 * The scope toggle lives here rather than in the engine's generic
 * `toggleFilter` because two behaviours are bound to it: it persists through
 * `useSidebarProjectPreferences` (shared with pin + order), and opening a
 * project the user isn't a member of flips it OFF for the session WITHOUT
 * overwriting the saved preference.
 */
export function useProjectsTreeProvider(): SidebarProviderResult {
  const { pathname } = useLocation();
  const { canBase } = usePermission();
  // The Meetings sub-row reads Calendar, so it follows Calendar access, not module.meetings.*.
  const { ready: calendarReady } = useCalendarShellAccess();
  // team_assignments.user_id is a string; the JWT subject is numeric.
  const userId = useAuthStore((s) => String(s.user?.sub ?? ''));
  const { data, isLoading } = useProjectsList();
  const {
    preferences,
    togglePin,
    reorder,
    setMineOnly: persistMineOnly,
    isPinned,
  } = useSidebarProjectPreferences(userId);

  // A user who can't view all projects always sees only their own, so there is
  // nothing to switch to and no toggle is rendered.
  const canViewAll = canBase(P.PROJECTS_VIEW_ALL);

  // Effective scope for this session, seeded from the persisted preference.
  // Ticking/unticking writes through so the choice survives a reload; the
  // auto-switch below deliberately does not.
  const [mineOnly, setMineOnlyState] = useState(preferences.mineOnly);
  const setMineOnly = useCallback(
    (next: boolean) => {
      setMineOnlyState(next);
      persistMineOnly(next);
    },
    [persistMineOnly],
  );
  const showMineOnly = canViewAll ? mineOnly : true;

  const [editingProject, setEditingProject] = useState<ProjectListItem | null>(null);
  const [editMode, setEditMode] = useState<'icon' | 'color' | null>(null);

  const all = useMemo(() => (data?.data ?? []) as ProjectListItem[], [data]);

  // "My Projects" = projects where the user is the PM or a member, i.e. holds
  // ANY team assignment. Critical for admins, whose list returns everything.
  const isMember = useCallback(
    (p: ProjectListItem) => p.team_assignments?.some((a) => a.user_id === userId) ?? false,
    [userId],
  );

  // Opening a project the user isn't a member of while the scope is "mine"
  // would hide it — switch to All so the just-opened project actually appears.
  // Session-only (setMineOnlyState): the user didn't untick anything, so this
  // must not overwrite their saved preference.
  const activeProjectId = pathname.match(/^\/projects\/([^/?]+)/)?.[1] ?? null;
  const [autoScopedFor, setAutoScopedFor] = useState<string | null>(null);
  if (activeProjectId && autoScopedFor !== activeProjectId) {
    const proj = all.find((p) => p.id === activeProjectId);
    if (proj) {
      setAutoScopedFor(activeProjectId);
      if (mineOnly && canViewAll && !isMember(proj)) setMineOnlyState(false);
    }
  }

  const projects = useMemo(() => {
    const base = showMineOnly ? all.filter(isMember) : all;
    return sortSidebarProjects(base, preferences.pinnedProjectIds, preferences.projectOrder);
  }, [showMineOnly, all, isMember, preferences]);

  const byId = useMemo(() => new Map(all.map((p) => [p.id, p])), [all]);

  const rows: RowConfig[] = projects.map((p) => {
    const badge = getUserProjectBadge(p.team_assignments, userId);
    return {
      id: p.id,
      label: p.name,
      to: `/projects/${p.id}?tab=tasks`,
      activePath: `/projects/${p.id}`,
      dynamicIcon: { name: p.icon ?? 'Folder', color: p.color ?? undefined },
      pinned: isPinned(p.id),
      pills: badge ? [badge] : undefined,
      expandable: {
        rows: PROJECT_TABS.filter((t) => t.id !== 'meetings' || calendarReady).map((t) => ({
          id: t.id,
          label: t.label,
          icon: TAB_ICON_NAME[t.id],
          // Sub-rows select the tab in the URL; the project row itself owns the
          // pathname-level active state via `activePath`. Overview is what the
          // detail page renders with no ?tab=, so it reads active there.
          toParam: {
            pathname: `/projects/${p.id}`,
            param: 'tab',
            value: t.id,
            isDefault: t.id === 'overview',
          },
          activeVariant: 'text' as const,
          // Tabs without a gating permission (Tasks) are always shown.
          visibleWhen:
            t.permission && t.id !== 'meetings' ? { projectPermission: t.permission } : undefined,
        })),
      },
    };
  });

  const headerControl: ReactNode = canViewAll ? (
    <PanelToggle
      label="My projects"
      ariaLabel="Show only projects where you have a role (PM, TL, …)"
      checked={mineOnly}
      onChange={setMineOnly}
    />
  ) : null;

  const wrapRow = (id: string, node: unknown): unknown => {
    const p = byId.get(id);
    if (!p) return node;
    return (
      <SidebarProjectContextMenu
        project={p}
        isPinned={isPinned(id)}
        onTogglePin={togglePin}
        onChangeIcon={(pr) => {
          setEditingProject(pr);
          setEditMode('icon');
        }}
        onChangeColor={(pr) => {
          setEditingProject(pr);
          setEditMode('color');
        }}
      >
        <div>{node as ReactNode}</div>
      </SidebarProjectContextMenu>
    );
  };

  const footer: ReactNode =
    editingProject && editMode ? (
      <SidebarProjectEditPopover
        open
        onOpenChange={(open: boolean) => {
          if (!open) {
            setEditingProject(null);
            setEditMode(null);
          }
        }}
        mode={editMode}
        project={editingProject}
      />
    ) : null;

  return {
    rows,
    isLoading,
    isError: false,
    emptyText: showMineOnly ? 'No projects assigned' : 'No projects',
    onReorder: reorder,
    wrapRow,
    headerControl,
    footer,
  };
}

/* ────────────────────────── Timesheet review ────────────────────────── */

/**
 * Projects the user can review timesheets for.
 *
 * The source app reads project names + role templates off its resolved
 * permission map; the ecosystem's permission cache stores only the booleans, so
 * the map is joined with the active project list for names, and the role
 * template comes from the list's team assignments.
 */
export function useTimesheetReviewProvider(): SidebarProviderResult {
  const { loaded, canBase, projectPermissions } = usePermission();
  const userId = useAuthStore((s) => String(s.user?.sub ?? ''));

  // `include_archived` is deliberate: archiving a project must NOT strand its
  // already-submitted timesheets with no reviewer surface. The server returns an
  // archived project here only while it still has unresolved timesheets, so the
  // rail empties itself once they are approved or rejected.
  const list = useProjectsList({
    is_active: true,
    include_archived: true,
    page: 1,
    page_size: 100,
  });

  const rows = useMemo<RowConfig[]>(() => {
    if (!loaded) return [];
    const reviewAll = canBase(P.TIMESHEET_REVIEW_ALL);

    return ((list.data?.data ?? []) as ProjectListItem[])
      .filter(
        (p) =>
          reviewAll || projectPermissions[p.id]?.permissions[P.TIMESHEET_REVIEW_PROJECT] === true,
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => {
        const mine = p.team_assignments?.find((a) => a.user_id === userId);
        const badge = getProjectBadgeByCode(mine?.template_code ?? null, mine?.template_name);
        return {
          id: p.id,
          label: p.name,
          icon: 'FolderOpen',
          to: `/projects/timesheets/review?project=${p.id}`,
          pills: [
            // Explains why a finished project is still in the review queue: its
            // timesheets are not resolved yet.
            ...(p.is_archived
              ? [{ label: 'ARCHIVED', className: 'bg-muted text-muted-foreground' }]
              : []),
            ...(badge ? [badge] : []),
          ],
          // "My projects" keeps only the projects the user holds a role on.
          flags: { mine: badge != null },
        };
      });
  }, [loaded, canBase, list.data, projectPermissions, userId]);

  return {
    rows,
    isLoading: !loaded || list.isLoading,
    isError: list.isError,
    emptyText: rows.length === 0 ? 'No projects to review' : 'No projects where you have a role',
  };
}
